<template>
  <div :class="$style.wrapper">
    <div :class="$style.photoRow">
      <div :class="$style.avatarWrap">
        <div v-if="avatarSrc" :class="$style.avatar">
          <img :src="avatarSrc" alt="" />
        </div>
        <div v-else :class="$style.avatar">{{ initial }}</div>
        <button
          :class="$style.editAvatarBtn"
          type="button"
          aria-label="Редактировать фото"
          @click="togglePhotoMenu"
        >
          <svg
            width="16"
            height="16"
            viewBox="0 0 16 16"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M11.5 2.5L13.5 4.5M3 13H5.5L12.25 6.25L9.75 3.75L3 10.5V13Z"
              stroke="currentColor"
              stroke-width="1.5"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
        </button>
      </div>

      <div :class="$style.photoMenuContainer">
        <input
          ref="galleryInput"
          type="file"
          accept="image/*"
          :class="$style.hiddenFileInput"
          @change="onGallerySelected"
        />

        <transition name="photo-menu-fade">
          <div
            v-if="isPhotoMenuOpen"
            :class="$style.photoMenuBackdrop"
            @click="closePhotoMenu"
          ></div>
        </transition>
        <transition name="photo-menu-pop">
          <div v-if="isPhotoMenuOpen" :class="$style.photoMenu">
            <button :class="$style.photoBtn" type="button" @click="openGallery">
              Загрузить фото
            </button>
            <button
              v-if="userHasPhoto || removePhoto"
              :class="[$style.photoBtn, $style.photoBtnDanger]"
              type="button"
              @click="deletePhoto"
            >
              Удалить фото
            </button>
          </div>
        </transition>
      </div>
    </div>

    <div :class="$style.field">
      <label :class="$style.label">Login</label>
      <input
        :class="[
          $style.input,
          loginTouched && !loginValid && $style.inputError,
        ]"
        type="text"
        v-model.trim="login"
        placeholder="@username"
        disabled
        @blur="loginTouched = true"
        @input="onFieldInput"
      />
    </div>

    <div :class="$style.field">
      <label :class="$style.label">Tel</label>
      <input
        ref="tel"
        :class="[
          $style.input,
          phoneTouched && !phoneValid && $style.inputError,
        ]"
        type="tel"
        inputmode="tel"
        autocomplete="tel"
        placeholder="+7 (___) ___-__-__"
        @blur="onPhoneBlur"
      />
      <div
        v-if="phoneTouched && !phoneValid && phone.length"
        :class="$style.error"
      >
        Введите номер в формате РФ
      </div>
    </div>

    <div :class="$style.field">
      <label :class="$style.label">Email</label>
      <input
        :class="[
          $style.input,
          emailTouched && !emailValid && $style.inputError,
        ]"
        type="email"
        v-model.trim="email"
        placeholder="maksim@example.com"
        autocomplete="email"
        inputmode="email"
        @blur="emailTouched = true"
        @input="onFieldInput"
      />
      <div v-if="emailTouched && !emailValid" :class="$style.error">
        Неверный формат email
      </div>
      <div
        v-else-if="emailTouched && emailValid && !emailRuZone"
        :class="$style.error"
      >
        Доступны только домены .ru, .su и .рф
      </div>
    </div>

    <button
      v-if="!cardButtonText"
      :class="$style.linkCard"
      type="button"
      @click="linkCard"
    >
      <img src="@/assets/img/account/personal-info/link-card.svg" alt="" />
      <span>Привязать карту или кошелек</span>
    </button>

    <button v-else :class="$style.linkCard" type="button">
      <span>{{ cardButtonText }}</span>
      <button
        type="button"
        :class="$style.deleteCardBtn"
        :disabled="isDeletingCard"
        @click.stop="deleteCard"
      >
        <span v-if="isDeletingCard" :class="$style.spinner"></span>
        <svg
          v-else
          width="16"
          height="16"
          viewBox="0 0 16 16"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M12 4L4 12M4 4L12 12"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </button>
    </button>

    <button :class="$style.delete" @click="deleteProfile">
      Удалить аккаунт
    </button>
  </div>
</template>

<script>
import IMask from "imask"

import { EMAIL_ZONE_MESSAGE, isRuZoneEmail } from "@/utils/emailZone"

export default {
  name: "PersonalInfo",
  data() {
    return {
      login: "",
      email: "",
      phone: "",
      isDeletingCard: false,

      origLogin: "",
      origEmail: "",
      origPhone: "",
      origPhotoUrl: "",
      newPhotoFile: null,
      removePhoto: false,
      photoObjectUrl: "",
      photoCacheBust: 0,
      photoCacheKey: "userPhotoCacheBust",
      isSaving: false,
      isPhotoMenuOpen: false,

      phoneValid: false,
      loginTouched: false,
      emailTouched: false,
      phoneTouched: false,

      mask: null,
      mbHandler: null,
    }
  },
  computed: {
    data() {
      return this.$store.state.data || {}
    },
    user() {
      return this.$store.state.user || {}
    },
    username() {
      return this.user.fullname || this.user.username || ""
    },
    initial() {
      return this.username?.trim()?.[0]?.toUpperCase() || ""
    },
    userHasPhoto() {
      return Boolean(this.user?.photo_url)
    },
    avatarSrc() {
      if (this.photoObjectUrl) return this.photoObjectUrl
      if (this.removePhoto) return null
      const url = this.user?.photo_url
      if (!url) return null
      if (!this.photoCacheBust) return url
      const sep = url.includes("?") ? "&" : "?"
      return `${url}${sep}v=${this.photoCacheBust}`
    },
    cardButtonText() {
      return this.user?.payment_method_title
    },
    loginValid() {
      return /^[a-zA-Z0-9._-]{3,20}$/.test(this.login || "")
    },
    emailValid() {
      return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(this.email || "")
    },
    emailRuZone() {
      return isRuZoneEmail(this.email)
    },
    allValid() {
      return this.loginValid && this.emailValid && this.phoneValid
    },
    isDirty() {
      return (
        this.login !== this.origLogin ||
        this.email !== this.origEmail ||
        this.phone !== this.origPhone ||
        Boolean(this.newPhotoFile) ||
        this.removePhoto
      )
    },
    canSave() {
      return this.allValid && this.isDirty
    },
  },
  watch: {
    user: {
      handler(u) {
        if (!u) return
        if (this.isDirty) return

        this.login = u.login || ""
        this.email = u.email || ""
        this.phone = u.phone || ""
        this.origPhotoUrl = u.photo_url || ""
        this.resetPhotoChanges()

        this.origLogin = this.login
        this.origEmail = this.email
        this.origPhone = this.phone

        this.$nextTick(() => {
          if (this.mask) {
            this.mask.value = this.phone || ""
            this.phoneValid = this.mask.masked.isComplete
            this.updateMB()
          }
        })
      },
      immediate: true,
      deep: true,
    },
  },

  methods: {
    syncPhotoCacheBustFromStorage() {
      try {
        const raw = window.localStorage?.getItem(this.photoCacheKey)
        const parsed = Number(raw)
        this.photoCacheBust = Number.isFinite(parsed) ? parsed : 0
      } catch (e) {
        // ignore
      }
    },
    persistPhotoCacheBust() {
      try {
        window.localStorage?.setItem(
          this.photoCacheKey,
          String(this.photoCacheBust)
        )
      } catch (e) {
        // ignore
      }
    },
    resetPhotoChanges() {
      this.newPhotoFile = null
      this.removePhoto = false
      if (this.photoObjectUrl) {
        URL.revokeObjectURL(this.photoObjectUrl)
        this.photoObjectUrl = ""
      }
    },
    openGallery() {
      this.closePhotoMenu()
      this.$refs.galleryInput?.click()
    },
    onGallerySelected(e) {
      const file = e?.target?.files?.[0]
      if (!file) return
      this.setPhotoFile(file)
      this.closePhotoMenu()
      e.target.value = ""
    },
    setPhotoFile(file) {
      this.removePhoto = false
      this.newPhotoFile = file

      if (this.photoObjectUrl) {
        URL.revokeObjectURL(this.photoObjectUrl)
      }
      this.photoObjectUrl = URL.createObjectURL(file)
      this.updateMB()
    },
    deletePhoto() {
      this.closePhotoMenu()
      this.newPhotoFile = null
      this.removePhoto = true
      if (this.photoObjectUrl) {
        URL.revokeObjectURL(this.photoObjectUrl)
        this.photoObjectUrl = ""
      }
      this.updateMB()
    },
    onPhoneBlur() {
      this.phoneTouched = true
      this.updateMB()
    },
    togglePhotoMenu() {
      this.isPhotoMenuOpen = !this.isPhotoMenuOpen
    },
    closePhotoMenu() {
      this.isPhotoMenuOpen = false
    },
    handlePhotoMenuScroll() {
      if (this.isPhotoMenuOpen) {
        this.isPhotoMenuOpen = false
      }
    },
    onFieldInput() {
      this.updateMB()
    },
    updateMB() {
      const mb = window.Telegram?.WebApp?.MainButton
      if (!mb) return
      mb.setText("Сохранить")
      if (this.isSaving) {
        if (mb.showProgress) {
          mb.showProgress(true)
        }
        if (mb.disable) {
          mb.disable()
        }
        return
      } else if (mb.hideProgress) {
        mb.hideProgress()
      }
      if (this.canSave) {
        mb.textColor = "#ffffff"
        mb.color = "#eb5a0a"
        mb.enable()
      } else {
        mb.textColor = "#B9B9BB"
        mb.color = "#E8E8EA"
        mb.enable()
      }
    },
    async save() {
      if (!this.canSave) return
      if (this.isSaving) return

      // Смена почты на домен вне .ru/.su/.рф не допускается (как и регистрация)
      if (this.email !== this.origEmail && !this.emailRuZone) {
        const tg = window.Telegram?.WebApp
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Внимание",
            message: EMAIL_ZONE_MESSAGE,
            buttons: [{ type: "close", text: "Понятно" }],
          })
        } else {
          window.alert(EMAIL_ZONE_MESSAGE)
        }
        return
      }

      this.isSaving = true
      this.updateMB()

      try {
        // Если изменился email (с login или без), требуется подтверждение почты
        if (this.email !== this.origEmail) {
          const payload = {
            login: this.login,
            email: this.email,
            phone: this.phone,
          }
          try {
            const response = await this.$store.dispatch(
              "registerStart",
              payload
            )

            // Если store action вернул ошибку (а не выбросил её), обрабатываем её
            if (
              response &&
              (response.response || response.status) &&
              response.status !== 200
            ) {
              const status =
                response?.response?.status ||
                response?.status ||
                (response?.response && response.response.status)
              const detail =
                response?.response?.data?.detail ||
                response?.data?.detail ||
                response?.detail

              const tg = window.Telegram?.WebApp

              // Обработка ошибки 409 - логин уже существует
              if (status === 409 && detail === "This user login exist.") {
                if (tg?.showPopup) {
                  tg.showPopup({
                    title: "Ошибка",
                    message: "Такой логин уже существует",
                    buttons: [{ type: "close", text: "Ок" }],
                  })
                } else {
                  window.alert("Такой логин уже существует")
                }
                return
              }

              // Обработка ошибки 409 - email уже существует
              if (
                status === 409 &&
                (detail === "This user email exist." ||
                  (typeof detail === "string" &&
                    detail.includes("email exist")))
              ) {
                if (tg?.showPopup) {
                  tg.showPopup({
                    title: "Ошибка",
                    message: "Такой email уже существует",
                    buttons: [{ type: "close", text: "Ок" }],
                  })
                } else {
                  window.alert("Такой email уже существует")
                }
                return
              }
            }

            if (response && response.status === 200) {
              const edit = true
              this.$router.push({
                path: "/check-email",
                state: { payload, edit },
              })
            }
          } catch (error) {
            console.error("Ошибка сохранения:", error)
            const tg = window.Telegram?.WebApp

            // Проверяем разные варианты структуры ошибки (error может быть возвращен из store, а не брошен)
            const status =
              error?.response?.status ||
              error?.status ||
              (error?.response && error.response.status)
            const detail =
              error?.response?.data?.detail ||
              error?.data?.detail ||
              error?.detail

            // Обработка ошибки 409 - логин уже существует
            if (status === 409 && detail === "This user login exist.") {
              if (tg?.showPopup) {
                tg.showPopup({
                  title: "Ошибка",
                  message: "Такой логин уже существует",
                  buttons: [{ type: "close", text: "Ок" }],
                })
              } else {
                window.alert("Такой логин уже существует")
              }
              return
            }

            // Обработка ошибки 409 - email уже существует
            if (
              status === 409 &&
              (detail === "This user email exist." ||
                (typeof detail === "string" && detail.includes("email exist")))
            ) {
              if (tg?.showPopup) {
                tg.showPopup({
                  title: "Ошибка",
                  message: "Такой email уже существует",
                  buttons: [{ type: "close", text: "Ок" }],
                })
              } else {
                window.alert("Такой email уже существует")
              }
              return
            }

            // Обработка других ошибок
            const errorMessage =
              detail ||
              error?.response?.data?.message ||
              error?.data?.message ||
              error?.message ||
              "Произошла ошибка при сохранении"
            if (tg?.showPopup) {
              tg.showPopup({
                title: "Ошибка",
                message: errorMessage,
                buttons: [{ type: "close", text: "Ок" }],
              })
            } else {
              window.alert(errorMessage)
            }
            return
          }
        } else {
          // Если email не изменился, обновляем через userUpdate (login, phone)
          const wasPhotoChange = Boolean(this.newPhotoFile) || this.removePhoto
          const payload = {
            phone: this.phone,
          }

          // Добавляем login в payload, если он изменился
          if (this.login !== this.origLogin) {
            payload.login = this.login
          }

          if (this.newPhotoFile) {
            payload.photo = this.newPhotoFile
          }

          if (this.removePhoto) {
            await this.$store.dispatch("deleteUserPhoto")
          }

          try {
            const result = await this.$store.dispatch("userUpdate", payload)
            // Если store action вернул ошибку (а не выбросил её), обрабатываем её
            if (result && (result.response || result.status || result.data)) {
              const status =
                result?.response?.status ||
                result?.status ||
                (result?.response && result.response.status)
              const detail =
                result?.response?.data?.detail ||
                result?.data?.detail ||
                result?.detail

              // Обработка ошибки 409 - логин уже существует
              if (status === 409 && detail === "This user login exist.") {
                const tg = window.Telegram?.WebApp
                if (tg?.showPopup) {
                  tg.showPopup({
                    title: "Ошибка",
                    message: "Такой логин уже существует",
                    buttons: [{ type: "close", text: "Ок" }],
                  })
                } else {
                  window.alert("Такой логин уже существует")
                }
                return
              }
            }
            await this.$store.dispatch("userMe")
            if (wasPhotoChange) {
              // photo_url часто остается тем же, поэтому форсим обновление img src
              this.photoCacheBust = Date.now()
              this.persistPhotoCacheBust()
            }
          } catch (error) {
            console.error("Ошибка сохранения:", error)
            const tg = window.Telegram?.WebApp

            // Проверяем разные варианты структуры ошибки (error может быть возвращен из store, а не брошен)
            const status =
              error?.response?.status ||
              error?.status ||
              (error?.response && error.response.status)
            const detail =
              error?.response?.data?.detail ||
              error?.data?.detail ||
              error?.detail

            // Обработка ошибки 409 - логин уже существует
            if (status === 409 && detail === "This user login exist.") {
              if (tg?.showPopup) {
                tg.showPopup({
                  title: "Ошибка",
                  message: "Такой логин уже существует",
                  buttons: [{ type: "close", text: "Ок" }],
                })
              } else {
                window.alert("Такой логин уже существует")
              }
              return
            }

            // Обработка других ошибок
            const errorMessage =
              detail ||
              error?.response?.data?.message ||
              error?.data?.message ||
              error?.message ||
              "Произошла ошибка при сохранении"
            if (tg?.showPopup) {
              tg.showPopup({
                title: "Ошибка",
                message: errorMessage,
                buttons: [{ type: "close", text: "Ок" }],
              })
            } else {
              window.alert(errorMessage)
            }
            return
          }
        }

        this.origLogin = this.login
        this.origEmail = this.email
        this.origPhone = this.phone
        this.origPhotoUrl = this.user?.photo_url || ""
        this.resetPhotoChanges()
        this.updateMB()
      } finally {
        this.isSaving = false
        this.updateMB()
      }
    },
    async deleteProfile() {
      // Уточняющий вопрос перед удалением аккаунта
      const tg = window.Telegram?.WebApp
      const confirmed = await new Promise((resolve) => {
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Удаление аккаунта",
            message: "Вы действительно хотите удалить аккаунт?",
            buttons: [
              { id: "yes", type: "destructive", text: "Да" },
              { id: "no", type: "cancel", text: "Нет" },
            ],
          }, (id) => resolve(id === "yes"))
        } else {
          resolve(window.confirm("Вы действительно хотите удалить аккаунт?"))
        }
      })
      if (!confirmed) return

      await this.$store.dispatch("userDelete")
      window.Telegram.WebApp.close()
    },
    async linkCard() {
      try {
        const response = await this.$store.dispatch("updatePayment")
        const paymentUrl = response?.data?.payment_url || response?.payment_url

        if (paymentUrl) {
          const tg = window.Telegram?.WebApp
          if (tg?.openLink) {
            tg.openLink(paymentUrl)
          } else {
            window.open(paymentUrl, "_blank")
          }
        } else {
          throw new Error("Не получена ссылка на оплату")
        }
      } catch (error) {
        console.error("Ошибка при привязке карты:", error)
        const tg = window.Telegram?.WebApp
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Не удалось привязать карту или кошелек"
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Ошибка",
            message: errorMessage,
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert(errorMessage)
        }
      }
    },
    async deleteCard() {
      if (this.isDeletingCard) return

      this.isDeletingCard = true
      try {
        await this.$store.dispatch("deletePayment")
        await this.$store.dispatch("userMe")
      } catch (error) {
        console.error("Ошибка при удалении карты:", error)
        const tg = window.Telegram?.WebApp
        const errorMessage =
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "Не удалось удалить карту"
        if (tg?.showPopup) {
          tg.showPopup({
            title: "Ошибка",
            message: errorMessage,
            buttons: [{ type: "close", text: "Ок" }],
          })
        } else {
          window.alert(errorMessage)
        }
      } finally {
        this.isDeletingCard = false
      }
    },
  },
  mounted() {
    this.$store.dispatch("userMe")
    this.syncPhotoCacheBustFromStorage()

    this.mask = IMask(this.$refs.tel, {
      mask: "+{7} (000) 000-00-00",
      lazy: false,
      overwrite: true,
      autofix: true,
      placeholderChar: "_",
    })

    if (this.phone) {
      this.$nextTick(() => {
        this.mask.value = this.phone
        this.phoneValid = this.mask.masked.isComplete
      })
    }

    this.mask.on("accept", () => {
      this.phone = this.mask.value
      this.phoneValid = this.mask.masked.isComplete
      this.updateMB()
    })

    this.mask.on("complete", () => {
      this.phone = this.mask.value
      this.phoneValid = true
      this.updateMB()
    })

    const mb = window.Telegram?.WebApp?.MainButton
    if (mb) {
      mb.setText("Сохранить")
      mb.show()
      this.mbHandler = () => this.save()
      mb.onClick(this.mbHandler)
      this.updateMB()
    }

    window.addEventListener("scroll", this.handlePhotoMenuScroll, true)
  },
  beforeUnmount() {
    if (this.mask) this.mask.destroy()
    if (this.photoObjectUrl) {
      URL.revokeObjectURL(this.photoObjectUrl)
      this.photoObjectUrl = ""
    }

    const tg = window.Telegram?.WebApp
    const mb = tg?.MainButton
    if (mb) {
      if (this.mbHandler) {
        mb.offClick(this.mbHandler)
        this.mbHandler = null
      }
      mb.hide()
    }

    window.removeEventListener("scroll", this.handlePhotoMenuScroll, true)
  },
}
</script>

<style lang="scss" module>
.wrapper {
  padding: 2rem 1rem 1rem;
  margin: 0 auto;
  display: grid;
}

.photoRow {
  display: flex;
  align-items: center;
  gap: 1rem;
  justify-content: center;
  margin: 0 auto 1.25rem auto;
  width: 100%;
}

.avatarWrap {
  position: relative;
  flex: 0 0 auto;
}

.avatar {
  width: 7.5rem;
  height: 7.5rem;
  border-radius: 50%;
  background: #111;
  color: #fff;
  display: grid;
  place-items: center;
  font-size: 2.75rem;
  font-weight: 500;
  flex: 0 0 auto;

  img {
    width: 7.5rem;
    height: 7.5rem;
    border-radius: 50%;
    object-fit: cover;
  }
}

.editAvatarBtn {
  position: absolute;
  right: 0;
  bottom: 0;
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  border: 1px solid #f0f0f2;
  background: #ffffff;
  color: #161616;
  display: grid;
  place-items: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
}

.photoMenuContainer {
  position: relative;
  flex: 0 0 auto;
}

.photoMenuBackdrop {
  position: fixed;
  inset: 0;
  background: transparent;
  z-index: 9;
}

.photoMenu {
  position: absolute;
  top: 4.5rem;
  right: 1rem;
  left: auto;
  transform: translateY(-0.25rem);
  display: grid;
  gap: 0.5rem;
  padding: 0.75rem;
  border-radius: 0.75rem;
  background: #ffffff;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.12);
  z-index: 10;
  min-width: 10.5rem;
  max-width: calc(100vw - 2rem);
}

:global(.photo-menu-fade-enter-active),
:global(.photo-menu-fade-leave-active) {
  transition: opacity 0.15s ease;
}

:global(.photo-menu-fade-enter-from),
:global(.photo-menu-fade-leave-to) {
  opacity: 0;
}

:global(.photo-menu-pop-enter-active),
:global(.photo-menu-pop-leave-active) {
  transition: opacity 0.18s ease, transform 0.18s ease;
}

:global(.photo-menu-pop-enter-from),
:global(.photo-menu-pop-leave-to) {
  opacity: 0;
  transform: translateY(-0.5rem) scale(0.98);
}

.photoBtn {
  border: 1px solid #f0f0f2;
  background: #f6f6f7;
  color: #161616;
  border-radius: 0.625rem;
  padding: 0.5rem 0.75rem;
  font-size: 0.875rem;
  cursor: pointer;
  text-align: center;
  width: 100%;
}

.photoBtnDanger {
  border-color: rgba(255, 59, 48, 0.22);
  color: #ff3b30;
}

.hiddenFileInput {
  display: none;
}

@media (max-width: 380px) {
  .photoRow {
    flex-direction: column;
    align-items: center;
    text-align: center;
  }

  .photoBtn {
    text-align: center;
  }
}

.field {
  width: 100%;
  display: grid;
  gap: 0.25rem;
  margin: 0 0 1rem 0;
}

.label {
  color: #8f8f94;
  font-size: 0.875rem;
  margin: 0 0 0 1rem;
}

.input {
  border-radius: 0.625rem;
  border: 1px solid #f0f0f2;
  background: #f6f6f7;
  padding: 0.625rem 1rem;
  font-size: 1rem;
  outline: none;
  caret-color: #000000;
  color: #161616;
  transition: border 0.2s, box-shadow 0.2s;
}

.input::placeholder {
  color: #b7b7bc;
}

.inputError {
  border-color: #ff6b6b;
}

.error {
  color: #ff3b30;
  font-size: 0.75rem;
  margin-top: 0.25rem;
}

.linkCard {
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  background: #ec6608;
  border-radius: 0.625rem;
  height: 3.25rem;
  width: 100%;
  font-size: 1rem;
  color: #fff;
  border: none;
  margin: 1.25rem 0 0.625rem 0;

  img {
    margin: 0 0.5rem 0 0;
  }
}

.deleteCardBtn {
  position: absolute;
  right: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  color: #fff;
  cursor: pointer;
  padding: 0.25rem;
  width: 1.5rem;
  height: 1.5rem;
  border-radius: 50%;
  transition: background-color 0.2s;

  &:hover:not(:disabled) {
    background-color: rgba(255, 255, 255, 0.1);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.7;
  }

  svg {
    width: 100%;
    height: 100%;
  }
}

.spinner {
  width: 1rem;
  height: 0.75rem;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #fff;
  border-radius: 100%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.delete {
  background: #eb281d;
  border-radius: 0.625rem;
  height: 3.25rem;
  width: 100%;
  font-size: 1rem;
  color: #fff;
  border: none;
}
</style>
